<template>
  <div class="sp-rating" part="rating" :style="glyphImages">
    <component :is="starsTag" v-bind="starsAttributes" class="sp-rating__stars" part="stars" :aria-label="starsLabel">
      <i v-for="(glyph, index) in stars" :key="index" class="star" :class="glyph.class" aria-hidden="true"></i>
    </component>

    <component :is="linkTag" v-if="!noScore" v-bind="linkAttributes" class="sp-rating__score" part="score">
      {{ score }}
    </component>

    <a
      v-if="infoUrl"
      class="sp-rating__info-icon"
      part="info-icon"
      :href="infoUrl"
      v-bind="targetAttributes"
      :aria-label="tAccessibleName('rating_info')"
    >
      <sp-icon name="help-circle-filled" aria-hidden="true"></sp-icon>
    </a>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { t, tAccessibleName } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { props as ratingProps } from "./props.js";
import fullStarSource from "./assets/star.svg?raw";
import halfStarSource from "./assets/star-half.svg?raw";

function svgUrl(source) {
  return `url("data:image/svg+xml,${encodeURIComponent(source.trim())}")`;
}

const props = defineProps(ratingProps);

const noScore = computed(() => toBoolean(props.noScore));

const MAX_RATING = 5;

const rating = computed(() => {
  const value = Number(props.rating);

  return Number.isFinite(value) && value > 0 ? Math.min(value, MAX_RATING) : 0;
});

// No empty stars follow the score: a rating of three draws three stars.
const stars = computed(() => {
  const whole = Math.floor(rating.value);
  const glyphs = Array.from({ length: whole }, () => ({ class: "full-star" }));
  const remainder = rating.value - whole;

  if (remainder >= 0.75) {
    glyphs.push({ class: "full-star" });
  } else if (remainder >= 0.25) {
    glyphs.push({ class: "half-star" });
  }

  return glyphs;
});

// An empty no-rating wording falls through to zero.
const score = computed(() => rating.value || (props.noRating ?? t("rating_not_rated")) || 0);

const starsLabel = computed(() =>
  rating.value ? tAccessibleName("rating_stars_label", { score: rating.value, max: MAX_RATING }) : score.value,
);

const glyphImages = {
  "--sp-rating-full-star-image": svgUrl(fullStarSource),
  "--sp-rating-half-star-image": svgUrl(halfStarSource),
};

const linkTag = computed(() => (props.url ? "a" : "span"));

const SAME_CONTEXT_TARGETS = ["_self", "_top", "_parent"];

const targetAttributes = computed(() => {
  if (!props.target) {
    return {};
  }

  if (SAME_CONTEXT_TARGETS.includes(props.target)) {
    return { target: props.target };
  }

  return { target: props.target, rel: "noopener" };
});

const linkAttributes = computed(() => (props.url ? { href: props.url, ...targetAttributes.value } : {}));

// An empty link is a zero-size tab stop, so the stars link only while there are stars to click.
const starsTag = computed(() => (stars.value.length ? linkTag.value : "span"));
const starsAttributes = computed(() => (stars.value.length ? linkAttributes.value : {}));
</script>

<style>
:host {
  --rating-icon-size: var(--sp-comp-rating-icon-size, 16px);
  --rating-info-icon-size: var(--sp-comp-rating-info-icon-size, 16px);
  --rating-info-icon-color: var(--sp-comp-rating-info-icon-color, var(--sp-sys-color-primary));
  --rating-font-size: var(--sp-comp-rating-font-size, 12px);
  --rating-font-size-sm: var(--sp-comp-rating-font-size-sm, 14px);
  --rating-line-height: var(--sp-comp-rating-line-height, 1.375);
  --rating-vertical-spacing: var(--sp-comp-rating-vertical-spacing, 7px);
  --rating-horizontal-spacing: var(--sp-comp-rating-horizontal-spacing, calc(var(--sp-ref-scale-2) / 2 * 3));

  display: block;
}
</style>

<style scoped>
.sp-rating {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  margin: var(--rating-vertical-spacing) 0;
}

a {
  text-decoration: none;
}

.sp-rating__stars {
  display: flex;
}

.star {
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: inline-block;
  width: var(--rating-icon-size);
  height: var(--rating-icon-size);
  min-width: var(--rating-icon-size);
  min-height: var(--rating-icon-size);
  margin: 0 1px;
}

.full-star {
  background-image: var(--sp-rating-full-star-image);
}

.half-star {
  background-image: var(--sp-rating-half-star-image);
}

.sp-rating__score {
  color: inherit;
  margin: 0 var(--rating-horizontal-spacing);
  font-size: var(--rating-font-size);
  line-height: var(--rating-line-height);
  white-space: nowrap;
  height: 1.11em;
}

/* $breakpoint-sm from assets/stylesheets/mixins/_breakpoint.scss. */
@media (min-width: 751px) {
  .sp-rating__score {
    font-size: var(--rating-font-size-sm);
  }
}

.sp-rating__info-icon {
  line-height: 0;
  width: var(--rating-info-icon-size);
  height: var(--rating-info-icon-size);
  min-width: var(--rating-info-icon-size);
  min-height: var(--rating-info-icon-size);
  vertical-align: middle;
}

.sp-rating__info-icon sp-icon {
  --sp-comp-icon-size: var(--rating-info-icon-size);
  --sp-comp-icon-fill: var(--rating-info-icon-color);

  display: block;
}
</style>
